/**
 * JSON-LD is written by pages as plain objects, and a page does not know its
 * own origin or language prefix — the same component serves `/telc/zadania`,
 * `/uk/telc/zadania` and `/en/telc/zadania`. `BreadcrumbList` needs each
 * `item` as an absolute URL (Google: "Invalid URL in field id"), so a page
 * writes the path without prefix and this fills in the rest.
 */
import { localize } from "@/lib/router";

interface Crumb {
  "@type"?: string;
  item?: unknown;
  [key: string]: unknown;
}

/** Makes every `BreadcrumbList` item an absolute, localised URL. A crumb with
 *  no `item` is the current page and gets `here`. Other objects pass through. */
export function absolutizeBreadcrumbs(
  schema: object[],
  origin: string,
  locale: string,
  here: string,
): object[] {
  return schema.map((node) => {
    const list = node as { "@type"?: string; itemListElement?: Crumb[] };
    if (list["@type"] !== "BreadcrumbList" || !Array.isArray(list.itemListElement)) return node;
    return {
      ...list,
      itemListElement: list.itemListElement.map((crumb) => {
        if (crumb.item === undefined) return { ...crumb, item: here };
        if (typeof crumb.item === "string" && crumb.item.startsWith("/")) {
          return { ...crumb, item: origin + localize(crumb.item, locale) };
        }
        return crumb;
      }),
    };
  });
}

/** What goes into the one `<script type="application/ld+json">`. Several nodes
 *  are one `@graph` under a single `@context`, never a bare array: a reader that
 *  takes `["@context"]` off the root (a crawler, an extension) throws on an array. */
export function toJsonLd(schema: object[]): object {
  if (schema.length === 1) return schema[0]!;
  const graph = schema.map((node) => {
    const { "@context": _context, ...rest } = node as Record<string, unknown>;
    return rest;
  });
  return { "@context": "https://schema.org", "@graph": graph };
}
